// src/api/promptService.js
import apiClient from './axiosConfig';

const API_URL = process.env.REACT_APP_API_URL || '';

const handleApiError = (error, operation) => {
  console.error(`${error.config?.method?.toUpperCase()} ${error.config?.url} - Error ${operation}:`, error);
  let message = 'An unknown error occurred.';
  const status = error.response?.status;

  if (error.response?.data) {
    const d = error.response.data;
    if (typeof d.detail === 'string') {
        message = d.detail;
    } else if (Array.isArray(d.detail)) {
        message = d.detail.map(err => {
            const loc = err.loc?.slice(1).join('.') || 'field';
            return `${loc}: ${err.msg}`;
        }).join('; ');
         if (!message) message = "Validation error";
    } else if (typeof d === 'string' && d.length < 500) {
        message = d;
    } else if (d.message && typeof d.message === 'string') {
        message = d.message;
    } else if (status) {
         message = `Request failed with status code ${status}`;
    }
     if (typeof message === 'string' && message.trim().startsWith('<')) {
         message = `Request failed with status code ${status || 'unknown'}`;
     }
  } else if (error.message) {
    message = error.message;
  }

  return {
    isError: true,
    message,
    status: status,
    originalError: error
  };
};

export const promptService = {
  /**
   * GET /api/v1/prompts
   * @param {object} filters - Optional filters like { agent_id: string (UUID), unlinked_only: boolean }
   */
  getAllPrompts: async (filters = {}) => {
    try {
      const queryParams = {};
      if (filters.agent_id) {
        queryParams.agent_id = filters.agent_id;
      }
      if (filters.hasOwnProperty('unlinked_only')) { // Check explicitly for boolean
        queryParams.unlinked_only = filters.unlinked_only;
      }
      const { data } = await apiClient.get(`${API_URL}/api/v1/prompts`, { params: queryParams });
      return data; // Array<PromptResponse>
    } catch (err) {
      return handleApiError(err, 'listing prompts');
    }
  },

  /** GET /api/v1/prompts/{prompt_id} */
  getPromptById: async (promptId) => {
    try {
      const { data } = await apiClient.get(
        `${API_URL}/api/v1/prompts/${promptId}`
      );
      return data; // PromptResponse
    } catch (err) {
      return handleApiError(err, `fetching prompt ${promptId}`);
    }
  },

  /**
   * POST /api/v1/prompts
   * @param {object} promptData - PromptCreate model: { name: string, content: string, agent_id?: string (UUID for initial link) }
   */
  createPrompt: async (promptData) => {
    try {
      // Backend expects: name, content, optionally agent_id.
      const payload = {
        name: promptData.name,
        content: promptData.content,
      };
      if (promptData.agent_id) {
        payload.agent_id = promptData.agent_id;
      }

      const { data } = await apiClient.post(
        `${API_URL}/api/v1/prompts`,
        payload
      );
      return data; // PromptResponse
    } catch (err) {
      return handleApiError(err, 'creating prompt');
    }
  },

  /**
   * PUT /api/v1/prompts/{prompt_id}
   * @param {string} promptId
   * @param {object} promptData - PromptUpdate model: { name?: string, content?: string }
   * Cannot update agent_id or activation status here.
   */
  updatePrompt: async (promptId, promptData) => {
    try {
      // Only name and content are updatable for a prompt directly.
      const payload = {};
      if (promptData.hasOwnProperty('name')) {
        payload.name = promptData.name;
      }
      if (promptData.hasOwnProperty('content')) {
        payload.content = promptData.content;
      }

      const { data } = await apiClient.put(
        `${API_URL}/api/v1/prompts/${promptId}`,
        payload
      );
      return data; // PromptResponse
    } catch (err) {
      return handleApiError(err, `updating prompt ${promptId}`);
    }
  },

  /**
   * DELETE /api/v1/prompts/{prompt_id}
   * Fails with 409 Conflict if the prompt is currently linked to an agent.
   */
  deletePrompt: async (promptId) => {
    try {
      await apiClient.delete(`${API_URL}/api/v1/prompts/${promptId}`);
      return { success: true }; // 204 No Content
    } catch (err) {
      return handleApiError(err, `deleting prompt ${promptId}`);
    }
  },

  // Global activatePrompt is REMOVED as activation is now per-agent
  // (Old function was here)
};